/**
 * TaskPriorityBadge Component
 * Displays task priority with color coding and icon
 * Fetches name/color from API cache when given only an ID
 */

import { Badge, BadgeProps } from '@mantine/core';
import {
  IconArrowDown,
  IconMinus,
  IconArrowUp,
} from '@tabler/icons-react';
import { useTaskLookups } from '@/hooks/tasks/useTaskLookups';

interface TaskPriorityObj {
  priorityID: number;
  name: string;
  color?: string;
}

interface TaskPriorityBadgeProps extends Omit<BadgeProps, 'children'> {
  priority?: TaskPriorityObj | number;
  priorityId?: number;
  showIcon?: boolean;
}

const PRIORITY_ICONS: Record<number, React.ReactNode> = {
  [0]: <IconArrowDown size={12} />,
  [1]: <IconMinus size={12} />,
  [2]: <IconArrowUp size={12} />,
};

const HEX_TO_MANTINE: Record<string, string> = {
  '#00FF00': 'green',
  '#FFFF00': 'yellow',
  '#FF0000': 'red',
};

function hexToMantineColor(hex?: string | null): string {
  if (!hex) return 'gray';
  return HEX_TO_MANTINE[hex.toUpperCase()] || 'gray';
}

export function TaskPriorityBadge({
  priority,
  priorityId,
  showIcon = true,
  variant = 'filled',
  size = 'sm',
  ...props
}: TaskPriorityBadgeProps) {
  const { getPriorityById } = useTaskLookups();

  const resolvedId = priorityId ?? (typeof priority === 'number' ? priority : priority?.priorityID);

  if (resolvedId === undefined || resolvedId === null) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  let label: string;
  let color: string;

  if (typeof priority === 'object' && priority?.name) {
    label = priority.name;
    color = hexToMantineColor(priority.color);
  } else {
    const apiPriority = getPriorityById(resolvedId);
    label = apiPriority?.name ?? `Priority ${resolvedId}`;
    color = hexToMantineColor(apiPriority?.color);
  }

  const icon = PRIORITY_ICONS[resolvedId];

  return (
    <Badge
      variant={variant}
      size={size}
      color={color}
      leftSection={showIcon && icon ? icon : undefined}
      {...props}
    >
      {label}
    </Badge>
  );
}
